import React, { Fragment } from 'react';
import { Box } from '@mui/system';
import { useSelector } from 'react-redux';
import { VirtuosoGrid } from 'react-virtuoso';
import * as Styled from './FinishesGroup.styled';
import { definedTranslation } from '../../i18n';
import {
  currentColorwayIdSelector,
  selectedChildMaterialSelector,
} from '../../redux/selectors/materialsSelector';
import { FinishesGroupData } from '../../types/material';
import { CardWrapperProps, CardWrapper } from '../Card/CardWrapper';

interface FinishesGroupProps
  extends Omit<CardWrapperProps, 'cardIndex' | 'groupFinishes'> {
  item: FinishesGroupData;
  groupIndex: number;
  activeGroups: number[];
  onActiveGroupChange?: (value: number) => void;
  parentRef: HTMLElement | undefined;
  showAll?: boolean;
  isColorways?: boolean;
  collapseLimit?: number;
  loadingCartIds?: string[];
  withConfig?: boolean;
}

const FinishesGroup: React.FC<FinishesGroupProps> = ({
  item,
  groupIndex,
  activeGroups,
  onActiveGroupChange,
  clickedProductGridIndex,
  activeProduct,
  projects,
  currentProjectData,
  intl,
  cartItemsIds,
  wasModalTriggered,
  onProductClick,
  onAddToCart,
  parentRef,
  showAll = false,
  isColorways,
  collapseLimit = 4,
  loadingCartIds,
  withConfig,
}) => {
  const selectedChildMaterial = useSelector(selectedChildMaterialSelector);
  const selectedColorwayId = useSelector(currentColorwayIdSelector);
  const isActiveGroup = activeGroups?.includes(groupIndex) || showAll;
  const groupFinishes = item?.items || [];
  const virtuosoGroupHeight = groupFinishes.length > 0 ? window.innerHeight : 0;

  const getTranslatedTitle = (title: string) => {
    // Convert title to a suitable key format by removing spaces
    const titleId = title.replace(/\s+/g, '');

    try {
      return intl?.formatMessage({ id: titleId, defaultMessage: title });
    } catch {
      // If translation fails, return the original title
      return title;
    }
  };
  const labels = {
    more: intl?.formatMessage(definedTranslation.miniPDP.more),
    collapse: intl?.formatMessage(definedTranslation.miniPDP.collapse),
  };

  if (groupFinishes?.length === 0) {
    return <Fragment />;
  }

  const renderCard = (index: number) => {
    return (
      <CardWrapper
        isColorways={isColorways}
        cardIndex={index}
        groupFinishes={groupFinishes}
        clickedProductGridIndex={clickedProductGridIndex}
        activeProduct={activeProduct}
        projects={projects}
        currentProjectData={currentProjectData}
        intl={intl}
        cartItemsIds={cartItemsIds}
        wasModalTriggered={wasModalTriggered}
        onProductClick={onProductClick}
        onAddToCart={onAddToCart}
        currentMaterialId={
          selectedChildMaterial?.entityId || selectedColorwayId
        }
        loadingCartIds={loadingCartIds}
      />
    );
  };

  return (
    <Styled.MaterialsGroup key={item.title}>
      <Box width="100%" display="flex" marginBottom="20px">
        <Styled.GridHeadline>
          {getTranslatedTitle(item.title)}
        </Styled.GridHeadline>
        {item?.items?.length > collapseLimit && !showAll && (
          <Styled.CollapseButton
            iconDense
            color="secondary"
            variant="contained"
            size="medium"
            endIcon={isActiveGroup ? 'minus' : 'plus'}
            onClick={() => onActiveGroupChange?.(groupIndex)}
            style={{
              marginTop: withConfig ? '46px' : '0',
            }}
          >
            {isActiveGroup
              ? labels.collapse
              : `${item?.items?.length - collapseLimit} ${labels.more}`}
          </Styled.CollapseButton>
        )}
      </Box>
      <Box minHeight="222px">
        {!isActiveGroup || groupFinishes.length <= collapseLimit ? (
          <Box display="flex" flexWrap="wrap">
            {Array.from({ length: collapseLimit }).map((_, index) => (
              <div
                style={{ width: '25%', padding: '5px' }}
                key={groupFinishes[index]?.entityId}
              >
                {renderCard(index)}
              </div>
            ))}
          </Box>
        ) : (
          <VirtuosoGrid
            useWindowScroll
            customScrollParent={parentRef}
            overscan={300}
            style={{ height: virtuosoGroupHeight }}
            totalCount={isActiveGroup ? groupFinishes.length : collapseLimit}
            itemContent={(index) => renderCard(index)}
          />
        )}
      </Box>
    </Styled.MaterialsGroup>
  );
};

export default FinishesGroup;
